import { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'

import { Button, SkeletonList } from '../../../components/ui'
import { getLegalDocumentLabel } from '../lib/legalDocuments'
import LegalDocumentDialog from './LegalDocumentDialog'

const getDefaultItemTag = header => (header.isOptional ? '선택' : '필수')
const getDefaultItemDescription = () => ''

export default function LegalDocumentChecklist({
  error = '',
  getItemDescription = getDefaultItemDescription,
  getItemTag = getDefaultItemTag,
  headers,
  isLoading = false,
  onChange,
  onRetry,
  optionalNotice = '선택 항목에 동의하지 않아도 akive의 기본 서비스를 이용할 수 있어요.',
  selections,
  selectAllLabel = '전체 동의 (선택 항목 포함)',
  showSelectAll = false,
}) {
  const selectAllRef = useRef(null)
  const [openDocumentId, setOpenDocumentId] = useState(null)
  const selectedCount = headers.filter(header => selections[header.legalDocumentId]).length
  const allSelected = headers.length > 0 && selectedCount === headers.length

  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = selectedCount > 0 && !allSelected
    }
  }, [allSelected, selectedCount])

  const updateAll = checked => {
    onChange(headers.reduce((next, header) => ({
      ...next,
      [header.legalDocumentId]: checked,
    }), { ...selections }))
  }

  if (isLoading) {
    return (
      <ChecklistRoot aria-label="약관 및 정책을 불러오는 중">
        <SkeletonList rows={5} label="약관 및 정책을 불러오는 중" />
      </ChecklistRoot>
    )
  }

  if (error) {
    return (
      <LoadError role="alert">
        <p>{error}</p>
        {onRetry && <Button $variant="secondary" onClick={onRetry}>다시 시도</Button>}
      </LoadError>
    )
  }

  return (
    <ChecklistRoot role="group" aria-label="약관 및 정책 동의">
      {showSelectAll && (
        <SelectAllLabel>
          <input
            ref={selectAllRef}
            type="checkbox"
            checked={allSelected}
            onChange={event => updateAll(event.target.checked)}
          />
          <span>{selectAllLabel}</span>
        </SelectAllLabel>
      )}

      <AgreementGroup>
        {headers.map(header => {
          const description = getItemDescription(header)
          const detailLabel = header.documentType === 'AGE_14_CONFIRMATION'
            ? '내용 보기'
            : '전문 보기'
          return (
            <AgreementItem key={header.legalDocumentId}>
              <AgreementLine>
                <AgreementLabel>
                  <input
                    type="checkbox"
                    checked={Boolean(selections[header.legalDocumentId])}
                    onChange={event => onChange({
                      ...selections,
                      [header.legalDocumentId]: event.target.checked,
                    })}
                  />
                  <AgreementText>
                    <span>
                      <em>[{getItemTag(header)}]</em>{' '}
                      {getLegalDocumentLabel(header)}
                    </span>
                    {description && <small>{description}</small>}
                  </AgreementText>
                </AgreementLabel>
                <DetailButton
                  type="button"
                  aria-label={`${header.title} ${detailLabel}`}
                  onClick={() => setOpenDocumentId(header.legalDocumentId)}
                >
                  {detailLabel}
                </DetailButton>
              </AgreementLine>
            </AgreementItem>
          )
        })}
      </AgreementGroup>

      {optionalNotice && headers.some(header => header.isOptional) && (
        <OptionalNotice>
          {optionalNotice}
        </OptionalNotice>
      )}

      {openDocumentId && (
        <LegalDocumentDialog
          legalDocumentId={openDocumentId}
          onClose={() => setOpenDocumentId(null)}
        />
      )}
    </ChecklistRoot>
  )
}

const ChecklistRoot = styled.section`
  display: grid;
  gap: 14px;
  margin-top: 8px;
  padding-top: 26px;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
`

const SelectAllLabel = styled.label`
  display: flex;
  min-height: 48px;
  padding: 12px 14px;
  align-items: center;
  gap: 10px;
  border: 1px solid ${({ theme }) => theme.colors.primaryAccent};
  border-radius: ${({ theme }) => theme.radius.small};
  color: ${({ theme }) => theme.colors.text};
  background: ${({ theme }) => theme.colors.primarySoft};
  font-size: 0.86rem;
  font-weight: 750;

  input { width: 18px; height: 18px; flex: 0 0 auto; accent-color: ${({ theme }) => theme.colors.primaryFill}; }
`

const AgreementGroup = styled.div`
  overflow: hidden;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.small};
  background: ${({ theme }) => theme.colors.surface};
`

const AgreementItem = styled.div`
  padding: 13px 14px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};

  &:last-child { border-bottom: 0; }
`

const AgreementLine = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
`

const AgreementLabel = styled.label`
  display: flex;
  min-width: 0;
  align-items: flex-start;
  gap: 9px;
  color: ${({ theme }) => theme.colors.text};
  font-size: 0.82rem;
  line-height: 1.55;

  input { width: 17px; height: 17px; flex: 0 0 auto; margin-top: 2px; accent-color: ${({ theme }) => theme.colors.primaryFill}; }
  em { color: ${({ theme }) => theme.colors.primary}; font-style: normal; font-weight: 750; }
`

const AgreementText = styled.span`
  display: grid;
  min-width: 0;
  gap: 3px;

  small {
    color: ${({ theme }) => theme.colors.textMuted};
    font-size: 0.72rem;
    font-weight: 600;
    line-height: 1.45;
  }
`

const DetailButton = styled.button`
  flex: 0 0 auto;
  padding: 2px 0;
  color: ${({ theme }) => theme.colors.primary};
  border: 0;
  background: transparent;
  font-size: 0.72rem;
  font-weight: 700;
  text-decoration: underline;
  text-underline-offset: 3px;
`

const OptionalNotice = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 0.74rem;
  line-height: 1.55;
`

const LoadError = styled.div`
  display: grid;
  min-height: 220px;
  align-content: center;
  justify-items: center;
  gap: 12px;
  color: ${({ theme }) => theme.colors.textMuted};
  text-align: center;

  p { margin: 0; }
`
